/**
 * The job form's casing strings, as an array.
 *
 * ─── Why this module exists ─────────────────────────────────────────────────
 *
 * NewJobScreen carried string 1 in thirteen top-level form keys and string 2 in
 * twelve more, with every resolver, cascade and hydration branch written twice —
 * once per string, `casingSizeInches` beside `casingSizeInches2`. There was no
 * third set, so no tenant could enter a third string. `jobs` has no columns for
 * one either; `job_casing_strings` does, and as of the step 7 API the endpoints
 * carry a `casingStrings` array both ways.
 *
 * Everything here is PURE. No React, no network. The screen owns the state and
 * the child component owns the rendering; hydration, resolution, presence and
 * the length bounds live here so they can be tested without a DOM and so the
 * two hydration sites cannot drift apart — which is exactly what happened to
 * the pair they replace (the edit path read both key spellings, the duplicate
 * path read only camelCase).
 *
 * ─── The two shapes, and why they are different ─────────────────────────────
 *
 * `CasingStringForm` is UI state: a select's value is '' or a catalog entry or
 * the literal 'custom', with the real value parked in a `custom*` field.
 * `CasingStringPayload` is what the API stores: six nullable strings, no
 * sentinel. `resolveCasingString` is the one place the first becomes the second.
 */

import {
  CASING_SIZES,
  TUBING_SIZES,
  TUBING_GRADES,
  isTubingThread,
} from '../data/casingData';
import { normalizeWeightPpf, tubingWeightsForSize } from '../components/casing/useTorqueProfile';
import { canonicalTubingSize } from '../data/casingData';
import type { JobCasingString } from '../types/casing-string';

// ─── Reference data ─────────────────────────────────────────────────────────
// Moved here verbatim from NewJobScreen so the per-string child component can
// reach it without importing the screen. Unchanged in content.

export const CASING_WEIGHTS: Record<string, { lbFt: string; kgM: string; label: string }[]> = {
  '4.500': [
    { lbFt: '9.50', kgM: '14.14', label: '9.50 lb/ft' },
    { lbFt: '10.50', kgM: '15.63', label: '10.50 lb/ft' },
    { lbFt: '11.60', kgM: '17.26', label: '11.60 lb/ft' },
    { lbFt: '13.50', kgM: '20.09', label: '13.50 lb/ft' },
    { lbFt: '15.10', kgM: '22.47', label: '15.10 lb/ft' },
  ],
  '5.000': [
    { lbFt: '11.50', kgM: '17.11', label: '11.50 lb/ft' },
    { lbFt: '13.00', kgM: '19.35', label: '13.00 lb/ft' },
    { lbFt: '15.00', kgM: '22.32', label: '15.00 lb/ft' },
    { lbFt: '18.00', kgM: '26.79', label: '18.00 lb/ft' },
  ],
  '5.500': [
    { lbFt: '14.00', kgM: '20.83', label: '14.00 lb/ft' },
    { lbFt: '15.50', kgM: '23.07', label: '15.50 lb/ft' },
    { lbFt: '17.00', kgM: '25.30', label: '17.00 lb/ft' },
    { lbFt: '20.00', kgM: '29.76', label: '20.00 lb/ft' },
    { lbFt: '23.00', kgM: '34.23', label: '23.00 lb/ft' },
  ],
  '7.000': [
    { lbFt: '17.00', kgM: '25.30', label: '17.00 lb/ft' },
    { lbFt: '20.00', kgM: '29.76', label: '20.00 lb/ft' },
    { lbFt: '23.00', kgM: '34.23', label: '23.00 lb/ft' },
    { lbFt: '26.00', kgM: '38.69', label: '26.00 lb/ft' },
    { lbFt: '29.00', kgM: '43.16', label: '29.00 lb/ft' },
    { lbFt: '32.00', kgM: '47.62', label: '32.00 lb/ft' },
    { lbFt: '35.00', kgM: '52.09', label: '35.00 lb/ft' },
    { lbFt: '38.00', kgM: '56.55', label: '38.00 lb/ft' },
  ],
  '7.625': [
    { lbFt: '24.00', kgM: '35.72', label: '24.00 lb/ft' },
    { lbFt: '26.40', kgM: '39.29', label: '26.40 lb/ft' },
    { lbFt: '29.70', kgM: '44.20', label: '29.70 lb/ft' },
    { lbFt: '33.70', kgM: '50.15', label: '33.70 lb/ft' },
    { lbFt: '39.00', kgM: '58.04', label: '39.00 lb/ft' },
  ],
  '8.625': [
    { lbFt: '24.00', kgM: '35.72', label: '24.00 lb/ft' },
    { lbFt: '28.00', kgM: '41.67', label: '28.00 lb/ft' },
    { lbFt: '32.00', kgM: '47.62', label: '32.00 lb/ft' },
    { lbFt: '36.00', kgM: '53.58', label: '36.00 lb/ft' },
    { lbFt: '40.00', kgM: '59.53', label: '40.00 lb/ft' },
    { lbFt: '44.00', kgM: '65.48', label: '44.00 lb/ft' },
    { lbFt: '49.00', kgM: '72.92', label: '49.00 lb/ft' },
  ],
  '9.625': [
    { lbFt: '32.30', kgM: '48.07', label: '32.30 lb/ft' },
    { lbFt: '36.00', kgM: '53.58', label: '36.00 lb/ft' },
    { lbFt: '40.00', kgM: '59.53', label: '40.00 lb/ft' },
    { lbFt: '43.50', kgM: '64.74', label: '43.50 lb/ft' },
    { lbFt: '47.00', kgM: '69.95', label: '47.00 lb/ft' },
    { lbFt: '53.50', kgM: '79.62', label: '53.50 lb/ft' },
  ],
  '10.750': [
    { lbFt: '32.75', kgM: '48.74', label: '32.75 lb/ft' },
    { lbFt: '40.50', kgM: '60.27', label: '40.50 lb/ft' },
    { lbFt: '45.50', kgM: '67.71', label: '45.50 lb/ft' },
    { lbFt: '51.00', kgM: '75.90', label: '51.00 lb/ft' },
    { lbFt: '55.50', kgM: '82.60', label: '55.50 lb/ft' },
    { lbFt: '60.70', kgM: '90.33', label: '60.70 lb/ft' },
    { lbFt: '65.70', kgM: '97.77', label: '65.70 lb/ft' },
  ],
  '11.750': [
    { lbFt: '42.00', kgM: '62.50', label: '42.00 lb/ft' },
    { lbFt: '47.00', kgM: '69.95', label: '47.00 lb/ft' },
    { lbFt: '54.00', kgM: '80.36', label: '54.00 lb/ft' },
    { lbFt: '60.00', kgM: '89.29', label: '60.00 lb/ft' },
    { lbFt: '65.00', kgM: '96.73', label: '65.00 lb/ft' },
  ],
  '13.375': [
    { lbFt: '48.00', kgM: '71.44', label: '48.00 lb/ft' },
    { lbFt: '54.50', kgM: '81.11', label: '54.50 lb/ft' },
    { lbFt: '61.00', kgM: '90.78', label: '61.00 lb/ft' },
    { lbFt: '68.00', kgM: '101.20', label: '68.00 lb/ft' },
    { lbFt: '72.00', kgM: '107.15', label: '72.00 lb/ft' },
  ],
  '6.625': [
    { lbFt: '20.00', kgM: '29.76', label: '20.00 lb/ft' },
    { lbFt: '24.00', kgM: '35.72', label: '24.00 lb/ft' },
    { lbFt: '28.00', kgM: '41.67', label: '28.00 lb/ft' },
  ],
  '16.000': [
    { lbFt: '65.00', kgM: '96.73', label: '65.00 lb/ft' },
    { lbFt: '75.00', kgM: '111.61', label: '75.00 lb/ft' },
    { lbFt: '84.00', kgM: '125.00', label: '84.00 lb/ft' },
  ],
  '18.625': [
    { lbFt: '87.50', kgM: '130.21', label: '87.50 lb/ft' },
  ],
  '20.000': [
    { lbFt: '94.00', kgM: '139.89', label: '94.00 lb/ft' },
    { lbFt: '106.50', kgM: '158.49', label: '106.50 lb/ft' },
    { lbFt: '133.00', kgM: '197.92', label: '133.00 lb/ft' },
  ],
};

/**
 * Spelled EXACTLY as canonicalizeGrade() renders them (src/data/casingData.ts).
 * 'SOO55' and 'TN 80SS' were the two entries that did not match the canonical
 * form; the API rewrites any grade it is given, so a picker option that is not
 * canonical comes back from the server looking like a custom grade.
 */
export const CASING_GRADES = [
  'H-40', 'J-55', 'K-55', 'SOO-55', 'M-65', 'N-80', 'L-80', '13Cr L-80',
  'PS-80', 'TN-80SS', 'C-90', 'T-95', 'P-110', 'Q-125',
];

export const THREAD_TYPES = ['LTC', 'STC', 'BTC'];

/** Tubing weight (lb/ft) options for a size, derived from the API RP 5C1 Table 3 spec. */
function tubingWeightOptions(inches: string): { lbFt: string; kgM: string; label: string }[] {
  return tubingWeightsForSize(canonicalTubingSize(inches)).map((w) => ({
    lbFt: w.toFixed(2),
    kgM: (w * 1.4882).toFixed(2),
    label: `${w.toFixed(2)} lb/ft`,
  }));
}

/** Weight options for a string given its pipe type + selected size. */
export function weightOptionsFor(
  pipeType: string,
  inches: string,
): { lbFt: string; kgM: string; label: string }[] {
  if (!inches || inches === 'custom') return [];
  if (pipeType === 'tubing') return tubingWeightOptions(inches);
  return CASING_WEIGHTS[inches]
    || CASING_WEIGHTS[Object.keys(CASING_WEIGHTS).find((k) => Number(k) === Number(inches)) || '']
    || [];
}

// ─── Length bounds ──────────────────────────────────────────────────────────

/**
 * Mirrors CASING_STRING_FIELD_LIMITS in the API.
 *
 * TWO NUMBERS, because there are two destinations. `grade` and `thread_type` are
 * `text` columns and carry MAX_SPEC_TEXT_LENGTH's 200-character paste guard. The
 * four measurement fields land in `job_casing_strings.size_inches` and its
 * siblings, which are varchar(20) — so 20 is the column, not a guess.
 *
 * The API rejects past these with a 400 naming the string and the field. This
 * copy exists so the user is told before the round trip, and so a `maxLength`
 * can stop the twenty-first character being typed at all.
 */
export const MAX_CASING_MEASUREMENT_LENGTH = 20;
export const MAX_CASING_TEXT_LENGTH = 200;

/** Upper bound on strings per job. Mirrors MAX_CASING_STRINGS in the API. */
export const MAX_CASING_STRINGS = 20;

// ─── Types ──────────────────────────────────────────────────────────────────

/** One casing string as the FORM holds it. Select sentinels and all. */
export interface CasingStringForm {
  /**
   * Stable React key. NOT `string_number` and NOT the child row id.
   *
   * The row id is no good as a key: a string the user just added does not have
   * one yet. `stringNumber` is stable now but was not always, and keying on
   * identity rather than on a stored value keeps React's state tied to the
   * section the user is looking at whatever happens to the number.
   */
  key: string;
  /**
   * The string's STORED number (`job_casing_strings.string_number`), kept
   * through every edit — never re-derived from array position.
   *
   * ─── Why the form does not renumber (api#830) ─────────────────────────────
   *
   * The API upserts strings IN PLACE by number, and since 0424 a ticket's
   * actual metres point at a string's ROW. Positional renumbering therefore
   * turned "remove string 3 of 6" into "write string 4's spec over row 3 and
   * drop number 6": the ticket's metres stayed on row 3, now describing a
   * different pipe, and no 409 fired because the only number that disappeared
   * held none. Reproduced on local data: 1,500.25 m run on HUNTING WEDGE read
   * afterwards as TSH 441, and the save answered 200.
   *
   * With the stored number kept, removing string 3 removes number 3, and the
   * API's CASING_STRING_HAS_METRES refuses it by name while a ticket holds
   * metres on it. The cost is a visible gap (1, 2, 4), which the API allows and
   * which the field card already renders honestly.
   *
   * A NEW string takes a number no string in this form session has held — see
   * `nextCasingStringNumber` — because reusing a removed number is the same
   * in-place overwrite by another route. `null` only on a string built before
   * any number was assigned; `casingStringsPayload` numbers it then.
   */
  stringNumber: number | null;
  /**
   * UI-only, never persisted. Derived from the thread on load (NU/EUE/IJ =
   * tubing) exactly as the backend does. Chooses which size/grade/thread
   * catalogs the pickers show.
   */
  pipeType: 'casing' | 'tubing';
  sizeInches: string;
  sizeMm: string;
  weightLbFt: string;
  weightKgM: string;
  grade: string;
  threadType: string;
  threadTypeOther: string;
  customSizeInches: string;
  customSizeMm: string;
  customWeight: string;
  customGrade: string;
  /**
   * The four measurement fields exactly as the API sent them, or null on a
   * string the user just added.
   *
   * ─── Why an unchanged field must round-trip verbatim ────────────────────────
   *
   * Hydration CANONICALIZES a measurement so it can be matched against the
   * catalog: `normWeight` turns both '15.1' and '15.10' into '15.10', because
   * they are the same catalog entry. That is correct for matching and lossy for
   * saving — once hydrated, the form cannot tell which spelling the row held.
   *
   * Production holds both. 41 jobs carry a one-decimal weight and 31 a
   * two-decimal one, '15.1' beside '15.10', because the pre-array form ran every
   * save through `String(normalizeWeightPpf(parseFloat(x)))` — which returns a
   * NUMBER, so '15.10' came back one character shorter each time — while
   * `jobs.casing_weight_lb_ft` is varchar and kept whatever it was handed.
   * (String 2 is immune: numeric(6,2) normalizes on the way in. That asymmetry
   * is the fingerprint FieldCasingStringCard describes as "'13.5' and '23.00' on
   * two strings of the same job".)
   *
   * So sending the canonical form rewrites the 41, and sending the normalized
   * form rewrites the 31. Neither is byte-identical, and PAR opens these jobs
   * daily. The way out is not to pick a spelling: it is to send back what was
   * loaded whenever the user did not actually change the value. A real edit
   * still writes the new, canonical value.
   */
  loaded: {
    sizeInches: string | null;
    sizeMm: string | null;
    weightLbFt: string | null;
    weightKgM: string | null;
  } | null;
}

/** One casing string as the API stores it. Six nullable strings, no sentinels. */
export interface CasingStringPayload {
  stringNumber: number;
  sizeInches: string | null;
  sizeMm: string | null;
  weightLbFt: string | null;
  weightKgM: string | null;
  grade: string | null;
  threadType: string | null;
}

// ─── Construction ───────────────────────────────────────────────────────────

let keySeq = 0;
/** Monotonic and process-local. Only ever compared for React identity. */
function nextKey(): string {
  keySeq += 1;
  return `cs-${keySeq}`;
}

export function blankCasingString(stringNumber: number | null = null): CasingStringForm {
  return {
    key: nextKey(),
    stringNumber,
    pipeType: 'casing',
    sizeInches: '', sizeMm: '', weightLbFt: '', weightKgM: '',
    grade: '', threadType: '', threadTypeOther: '',
    customSizeInches: '', customSizeMm: '', customWeight: '', customGrade: '',
    loaded: null,
  };
}

// ─── Hydration ──────────────────────────────────────────────────────────────

/** '7' → '7.000', so a stored value matches a catalog entry. Non-numeric passes through. */
function normSize(raw: string): string {
  return raw && !isNaN(Number(raw)) ? Number(raw).toFixed(3) : raw;
}
/** '23' → '23.00'. Same purpose, two decimals. */
function normWeight(raw: string): string {
  return raw && !isNaN(Number(raw)) ? Number(raw).toFixed(2) : raw;
}

/**
 * One `job_casing_strings` row → form state.
 *
 * Every branch here was previously written twice in NewJobScreen, once against
 * `casing_*` and once against `casing_*_2`, with the second copy inline in an
 * IIFE per field. Same rules, stated once:
 *
 *   - pipe type from the thread, because it is not stored
 *   - a value the catalog knows selects that option; anything else selects
 *     'custom' and parks the real value in the matching custom* field
 *   - a custom size keeps its STORED mm rather than recomputing 25.4 × inches,
 *     because the user may have typed a specific one
 */
export function hydrateCasingString(row: Partial<JobCasingString>): CasingStringForm {
  const rawThread = row.thread_type || '';
  const pipeType: 'casing' | 'tubing' = isTubingThread(rawThread) ? 'tubing' : 'casing';
  const sizeTable = pipeType === 'tubing' ? TUBING_SIZES : CASING_SIZES;
  const gradeTable = pipeType === 'tubing' ? TUBING_GRADES : CASING_GRADES;

  // A thread outside the standard casing set renders through "Other". Tubing
  // threads (NU/EUE/IJ) flow through it too and display fine, because the
  // tubing-mode ConnectionTypeSelect lists them as options.
  let threadType = rawThread;
  let threadTypeOther = '';
  if (threadType && !THREAD_TYPES.includes(threadType)) {
    threadTypeOther = threadType;
    threadType = 'Other';
  }

  const sizeInches = normSize(row.size_inches || '');
  const sizeMatch = sizeTable.find((s) => s.inches === sizeInches || Number(s.inches) === Number(sizeInches));

  const weightLb = normWeight(row.weight_lb_ft || '');
  const weightMatch = weightOptionsFor(pipeType, sizeMatch ? sizeMatch.inches : sizeInches)
    .find((w) => w.lbFt === weightLb || Number(w.lbFt) === Number(weightLb));

  const grade = row.grade || '';
  const gradeKnown = gradeTable.includes(grade);

  const storedNumber = Number(row.string_number);
  return {
    key: nextKey(),
    stringNumber: Number.isInteger(storedNumber) && storedNumber >= 1 ? storedNumber : null,
    pipeType,
    sizeInches: sizeMatch ? sizeMatch.inches : (sizeInches ? 'custom' : ''),
    sizeMm: row.size_mm || '',
    weightLbFt: weightMatch ? weightMatch.lbFt : (weightLb ? 'custom' : ''),
    weightKgM: row.weight_kg_m || '',
    grade: gradeKnown ? grade : (grade ? 'custom' : ''),
    threadType,
    threadTypeOther,
    customSizeInches: !sizeMatch && sizeInches ? sizeInches : '',
    customSizeMm: !sizeMatch && row.size_mm ? row.size_mm : '',
    customWeight: !weightMatch && weightLb ? weightLb : '',
    customGrade: !gradeKnown && grade ? grade : '',
    loaded: {
      sizeInches: row.size_inches ?? null,
      sizeMm: row.size_mm ?? null,
      weightLbFt: row.weight_lb_ft ?? null,
      weightKgM: row.weight_kg_m ?? null,
    },
  };
}

/**
 * The twelve flat casing columns of a job → child-table rows.
 *
 * THE FALLBACK PATH, not the normal one. Since the step 7 API every job payload
 * carries `casingStrings`, and that array is the only thing that can express a
 * third string. This exists for a payload that predates it — most concretely a
 * job cached in IndexedDB by the offline layer before this deploy.
 *
 * Presence is ANY-FIELD, matching `casingStringHasData` in the API and 0289's
 * backfill predicate. The screen previously gated string 2 on
 * `casing_size_2_inches` alone, so a string carrying only a grade hydrated as
 * absent and the next save pruned it. Zero production jobs are in that shape —
 * it is reachable by picking a grade without a size — but the any-field rule is
 * both free here and the same rule the server applies.
 */
export function casingStringsFromFlatJob(src: Record<string, any>): JobCasingString[] {
  const pick = (snake: string, camel: string) => {
    const v = src?.[snake] !== undefined && src?.[snake] !== null ? src[snake] : src?.[camel];
    return v == null || v === '' ? null : String(v);
  };
  const rows: JobCasingString[] = [
    {
      string_number: 1,
      size_inches: pick('casing_size_inches', 'casingSizeInches'),
      size_mm: pick('casing_size_mm', 'casingSizeMm'),
      weight_lb_ft: pick('casing_weight_lb_ft', 'casingWeightLbFt'),
      weight_kg_m: pick('casing_weight_kg_m', 'casingWeightKgM'),
      grade: pick('casing_grade', 'casingGrade'),
      thread_type: pick('thread_type', 'threadType'),
    },
    {
      string_number: 2,
      size_inches: pick('casing_size_2_inches', 'casingSize2Inches'),
      size_mm: pick('casing_size_2_mm', 'casingSize2Mm'),
      weight_lb_ft: pick('casing_weight_2_lb_ft', 'casingWeight2LbFt'),
      weight_kg_m: pick('casing_weight_2_kg_m', 'casingWeight2KgM'),
      grade: pick('casing_grade_2', 'casingGrade2'),
      thread_type: pick('thread_type_2', 'threadType2'),
    },
  ];
  return rows.filter(casingStringRowHasData);
}

/** ANY of the six spec fields is populated. The API's `casingStringHasData`. */
export function casingStringRowHasData(row: Partial<JobCasingString>): boolean {
  return [row.size_inches, row.size_mm, row.weight_lb_ft, row.weight_kg_m, row.grade, row.thread_type]
    .some((v) => v != null && String(v).trim() !== '');
}

/**
 * A job payload → the form's casing strings.
 *
 * THE single entry point for both hydration sites: the edit path and
 * "duplicate the last job on this rig". They read different endpoints and used
 * to read different key spellings — the duplicate path was camelCase-only — so
 * a job that opened correctly for editing could still lose a value when
 * duplicated. One function, one rule.
 *
 * Never returns an empty array: a casing job has at least one string, and a
 * form with no sections has nothing to type into. A job with no casing data at
 * all gets one blank section, which is what it rendered before.
 */
export function hydrateCasingStrings(src: Record<string, any>): CasingStringForm[] {
  const rows: JobCasingString[] = Array.isArray(src?.casingStrings)
    ? [...(src.casingStrings as JobCasingString[])]
        .filter(casingStringRowHasData)
        .sort((a, b) => (a.string_number ?? 0) - (b.string_number ?? 0))
    : casingStringsFromFlatJob(src);

  const hydrated = rows.map(hydrateCasingString);
  return hydrated.length > 0 ? hydrated : [blankCasingString(1)];
}

/**
 * The same strings numbered 1..N by position — for a NEW job built from an old
 * one (the duplicate path). A new job has no rows, so there is nothing for a
 * renumber to overwrite, and carrying the source job's gaps would only puzzle.
 */
export function renumberForNewJob(strings: CasingStringForm[]): CasingStringForm[] {
  return strings.map((s, i) => ({ ...s, stringNumber: i + 1 }));
}

/**
 * The number for a string added in this form session.
 *
 * One above every number the session has seen — the loaded ones, the ones
 * added since, and the ones REMOVED since — so a removed number is never handed
 * to a new string. Reusing it would upsert the new spec over the removed row,
 * and any ticket metres on that row would silently describe the new pipe.
 *
 * Past the API's ceiling (20), the lowest number neither in the form nor ever
 * seen; failing that, the lowest one not in the form. That last case can reuse
 * a removed number, and is only reachable by a job that has cycled through all
 * twenty.
 */
export function nextCasingStringNumber(strings: CasingStringForm[], seen: Iterable<number>): number {
  const inForm = new Set(strings.map((s) => s.stringNumber).filter((n): n is number => n !== null));
  const ever = new Set<number>(Array.from(seen));
  inForm.forEach((n) => ever.add(n));
  const next = Math.max(0, ...Array.from(ever)) + 1;
  if (next <= MAX_CASING_STRINGS) return next;
  for (let n = 1; n <= MAX_CASING_STRINGS; n += 1) if (!inForm.has(n) && !ever.has(n)) return n;
  for (let n = 1; n <= MAX_CASING_STRINGS; n += 1) if (!inForm.has(n)) return n;
  return next;
}

// ─── Presence and resolution ────────────────────────────────────────────────

/**
 * The form-state form of the any-field rule.
 *
 * Reads the RESOLVED values so that a size of 'custom' with an empty
 * `customSizeInches` counts as empty — a section the user opened and never
 * filled in is not a string, and must not become a blank child row.
 */
export function casingStringFormHasData(s: CasingStringForm): boolean {
  const r = resolveCasingString(s, 1);
  return [r.sizeInches, r.sizeMm, r.weightLbFt, r.weightKgM, r.grade, r.threadType]
    .some((v) => v !== null);
}

/**
 * Form state → the API's shape.
 *
 * The resolvers are the pre-array ones, unchanged in behaviour — including
 * `normalizeWeightPpf`, which is why '11.6' and '11.60' resolve to one torque
 * profile, and the ×1.4882 kg/m derivation on a custom weight. Byte-identity on
 * an unchanged save depends on these staying exactly as they were: strings 1-2
 * are mirrored back into the twelve flat columns by the API, so a resolver that
 * rounded differently here would rewrite a live tenant's stored values.
 */
export function resolveCasingString(s: CasingStringForm, stringNumber: number): CasingStringPayload {
  const isCustomSize = s.sizeInches === 'custom';
  const isCustomWeight = isCustomSize || s.weightLbFt === 'custom';

  const sizeInches = isCustomSize ? s.customSizeInches : s.sizeInches;
  const sizeMm = isCustomSize ? s.customSizeMm : s.sizeMm;

  /**
   * A CATALOG weight is sent verbatim; only a hand-typed one is normalized.
   *
   * The pre-array form ran both through
   * `String(normalizeWeightPpf(parseFloat(x)))`, and `normalizeWeightPpf`
   * returns a NUMBER — `Math.round(w * 100) / 100` — so `String(13.5)` is
   * '13.5'. A catalog entry of '13.50' therefore came back one character
   * shorter every time the job was saved, and `jobs.casing_weight_lb_ft` is
   * varchar, so the shortened form is what got stored. Production carries 41
   * one-decimal and 31 two-decimal weights for that reason, '15.1' and '15.10'
   * among them — the same weight, two spellings, split by how many times the
   * job had been re-saved. String 2 is immune only because its column is
   * numeric(6,2) and normalizes on the way in.
   *
   * (This is the fingerprint FieldCasingStringCard's parseFloat comment
   * describes as "'13.5' and '23.00' on two strings of the same job".)
   *
   * Catalog values are already canonical two-decimal strings, so running one
   * through a float is a lossy no-op. Free text genuinely needs normalizing, so
   * that path is unchanged — a typed '11.6' still resolves to '11.6' and still
   * matches a '11.60' torque profile, because useTorqueProfile normalizes the
   * NUMBER it is given independently of the string stored here.
   */
  const weightLbFt = isCustomWeight
    ? (s.customWeight ? String(normalizeWeightPpf(parseFloat(s.customWeight))) : s.customWeight)
    : s.weightLbFt;
  /**
   * kg/m is derived from a hand-typed lb/ft, and falls back to the stored value
   * when there is nothing to derive it from.
   *
   * The pre-array form wrote `(parseFloat(customWeight || '0') * 1.4882).toFixed(2)`
   * down the custom branch, so a string with a custom SIZE but no weight typed
   * produced the literal '0.00' — a fabricated zero standing in for a weight
   * nobody entered, which is the one thing an absent value must never render as.
   * Smith J-0012 carries exactly that: kg/m '0.00' with no lb/ft at all.
   *
   * Falling back to `s.weightKgM` fixes both halves. A stored value survives an
   * unchanged save (J-0012 keeps its '0.00' rather than being cleared), and a
   * new string with no weight sends null rather than inventing one.
   */
  const weightKgM = isCustomWeight && s.customWeight
    ? (parseFloat(s.customWeight) * 1.4882).toFixed(2)
    : s.weightKgM;
  const grade = s.grade === 'custom' ? s.customGrade : s.grade;
  const threadType = s.threadType === 'Other' ? s.threadTypeOther : s.threadType;

  /**
   * Send the loaded spelling back when the value has not actually changed.
   *
   * "Not changed" is NUMERIC equality, not string equality — that is the whole
   * point. '15.1' and '15.10' are the same weight, hydration collapsed them to
   * one catalog selection, and re-canonicalizing on the way out would rewrite a
   * live tenant's stored value on a save the user thought was a no-op. A real
   * edit fails this test and writes the new value.
   *
   * Only the four measurement fields need it. `grade` and `threadType` are text
   * with no normalization in either direction, so they already round-trip.
   */
  const asLoaded = (resolved: string, key: keyof NonNullable<CasingStringForm['loaded']>) => {
    const was = s.loaded?.[key] ?? null;
    if (was === null || resolved === '') return resolved;
    if (was === resolved) return resolved;
    const a = Number(was);
    const b = Number(resolved);
    return Number.isFinite(a) && Number.isFinite(b) && a === b ? was : resolved;
  };

  return {
    stringNumber,
    sizeInches: asLoaded(sizeInches, 'sizeInches') || null,
    sizeMm: asLoaded(sizeMm, 'sizeMm') || null,
    weightLbFt: asLoaded(weightLbFt, 'weightLbFt') || null,
    weightKgM: asLoaded(weightKgM, 'weightKgM') || null,
    grade: grade || null,
    threadType: threadType || null,
  };
}

/**
 * The array to send on save.
 *
 * Empty sections are dropped and the survivors are numbered by POSITION, which
 * is what makes remove-and-renumber correct by construction: there is no stored
 * ordinal to get out of step, so removing the middle of three yields 1 and 2
 * with no gap and nothing for the API's unique index to collide on.
 *
 * ALWAYS SENT, including as `[]`. The API preserves ordinals 3+ when the key is
 * absent — right for a status-only PUT from another screen, wrong for this one.
 * This screen is the editor: if a user removed the third string, the array is
 * how the server finds out.
 */
export function casingStringsPayload(strings: CasingStringForm[]): CasingStringPayload[] {
  // Each string keeps its stored number — NOT its position. See `stringNumber`
  // on CasingStringForm for the ticket metres this protects. A string with no
  // number yet takes the lowest one no other string in the form holds.
  const withData = strings.filter(casingStringFormHasData);
  const used = new Set(withData.map((s) => s.stringNumber).filter((n): n is number => n !== null));
  let probe = 1;
  return withData.map((s) => {
    if (s.stringNumber !== null) return resolveCasingString(s, s.stringNumber);
    while (used.has(probe)) probe += 1;
    used.add(probe);
    return resolveCasingString(s, probe);
  });
}

/**
 * The loaded strings as the save would send them, for the edit screen to hold
 * and compare against. Built through `casingStringsPayload` so both sides of the
 * comparison pass through exactly the same resolution — comparing a form to the
 * raw stored rows would read '5.500' against '5.5' as an edit nobody made.
 */
export function casingStringsSnapshot(strings: CasingStringForm[]): string {
  return JSON.stringify(casingStringsPayload(strings));
}

/**
 * The `casingStrings` key for a save — or NO KEY, when an edit left casing alone.
 *
 * ─── WHY AN UNCHANGED ARRAY IS NOT SENT ────────────────────────────────────
 *
 * A present array is authoritative: the API overwrites every string from it. So
 * an office user who opened this form at 9:00 and saved a notes change at 9:10
 * used to send the 9:00 casing spec back — and silently undo a crew's 9:05
 * field correction of the grade (PUT /employee/my-jobs/:id/casing-strings/:n),
 * the correction that exists because the booked pipe was wrong. Nothing warned
 * anyone, and the torque window on the rig floor went back to the wrong number.
 *
 * An ABSENT array keeps what is stored — that is the API helper's existing
 * contract (strings 1-2 re-derived from the untouched columns, 3+ preserved from
 * the table), not a new one. So omitting it when nothing changed is the whole
 * fix, and it costs nothing when something did: an edit to casing still sends
 * the full array, removals included.
 *
 * What it does NOT fix, stated so nobody reads more into it: an office user who
 * DOES edit casing on a stale form still sends a full array and still replaces
 * the field correction. The API audits that write now, and the field card shows
 * who changed the spec last, so it is at least visible.
 *
 * `loaded` null means there is nothing to compare against — a create, a
 * duplicate, or an edit saved before the job loaded — and the array is sent, as
 * it always was.
 */
export function casingStringsForSave(
  current: CasingStringForm[],
  loaded: string | null,
): { casingStrings?: CasingStringPayload[] } {
  const payload = casingStringsPayload(current);
  if (loaded !== null && JSON.stringify(payload) === loaded) return {};
  return { casingStrings: payload };
}

// ─── Validation ─────────────────────────────────────────────────────────────

const MEASUREMENT_LABELS: [keyof CasingStringPayload, string][] = [
  ['sizeInches', 'Size (inches)'],
  ['sizeMm', 'Size (mm)'],
  ['weightLbFt', 'Weight (lb/ft)'],
  ['weightKgM', 'Weight (kg/m)'],
];
const TEXT_LABELS: [keyof CasingStringPayload, string][] = [
  ['grade', 'Grade'],
  ['threadType', 'Thread Type'],
];

/**
 * First over-length field across the strings, as a user-facing sentence.
 *
 * The API says the same thing with the same numbers; this is the courtesy copy
 * that stops the round trip. `maxLength` guards the inputs this screen owns,
 * but not `ConnectionTypeSelect`, which manages its own — so this is the
 * backstop that turns "500 on save" into a sentence naming the string.
 */
export function findCasingStringViolation(strings: CasingStringForm[]): string | null {
  const payload = casingStringsPayload(strings);
  for (const s of payload) {
    for (const [field, label] of MEASUREMENT_LABELS) {
      const v = s[field] as string | null;
      if (v && v.length > MAX_CASING_MEASUREMENT_LENGTH) {
        return `${label} on String ${s.stringNumber} is ${v.length} characters; the maximum is ${MAX_CASING_MEASUREMENT_LENGTH}.`;
      }
    }
    for (const [field, label] of TEXT_LABELS) {
      const v = s[field] as string | null;
      if (v && v.length > MAX_CASING_TEXT_LENGTH) {
        return `${label} on String ${s.stringNumber} is ${v.length} characters; the maximum is ${MAX_CASING_TEXT_LENGTH}.`;
      }
    }
  }
  if (payload.length > MAX_CASING_STRINGS) {
    return `A job may have at most ${MAX_CASING_STRINGS} casing strings.`;
  }
  return null;
}
